import { useContext, useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import { reportEvent } from '~/domains/analytics/events';
import type { EO_BRANDS } from '~/domains/auth/consts';
import { Routes } from '~/domains/common/constants/routes';
import { URLQueryKeys } from '~/domains/common/types/URLQueryKeys';
import useRedirectToEO from '~/domains/common/utils/useRedirectToEO';
import { ActiveTab as CommishActiveTab } from '~/domains/commissioner/enums';
import { useGetContestsRestarts } from '~/domains/commissioner/hooks/useGetContestsRestarts';
import { useDismissContestRestart } from '~/domains/commissioner/hooks/useDismissContestRestart';
import { UserContext } from '~/components/providers/UserProvider';

export const useHomeRestarts = () => {
  // Cache of dismissed contests ids, because backend can take some time to update the list
  const [dismissedContestIds, setDismissedContestIds] = useState<string[]>([]);

  const { userId } = useContext(UserContext);
  const router = useRouter();
  const { redirectToEO, isFetching, isRedirecting, requestedURL } = useRedirectToEO();
  const {
    contests,
    totalCount,
    isLoading: isRestartsLoading,
    isFetching: isRestartsFetching,
  } = useGetContestsRestarts({ userId });
  const { dismissContestRestart, isLoading: isDismissing } = useDismissContestRestart();

  const restartsProps = useMemo(() => {
    if (isRestartsLoading || !contests.length || !totalCount) return;

    return {
      content: {
        type: 'restart' as const,
        restarts: contests
          .filter((contest) => !dismissedContestIds.includes(contest.id))
          .map((contest) => ({
            isLoadingContest:
              (isFetching || isRedirecting) && requestedURL === contest.game_center_url,
            isLoadingActions: isDismissing && dismissedContestIds.includes(contest.id),
            id: contest.id,
            createdAt: new Date(),
            // V2 contests carry entitySource (lowercase 'ryp'/'ofp'), not source.
            // EO_BRANDS is uppercase, so normalize for the brand badge.
            source: (contest.source ?? contest.entitySource)?.toUpperCase() as keyof typeof EO_BRANDS,
            contest,
          })),
        onDismiss: (contestId: string) => {
          if (isDismissing || isRestartsFetching) return;

          const contest = contests?.find((c) => c.id === contestId);
          const isSplashV2 = contest?.entitySource === 'splash_v2';

          reportEvent('Home > Restarts > Dismiss', { count: totalCount });
          setDismissedContestIds((prev) => [...prev, contestId]);
          dismissContestRestart({ contestId, isSplashV2 });
        },
        onRestart: (restartUrl: string) => {
          if (isFetching || isRedirecting) return;

          reportEvent('Home > Restarts > Restart Contest', { count: totalCount });
          redirectToEO(restartUrl);
        },
        onContestClick: (gameCenterUrl: string) => {
          if (isFetching || isRedirecting) return;

          reportEvent('Home > Restarts > Click', { count: totalCount });
          redirectToEO(gameCenterUrl);
        },
      },
      totalCount,
      onViewAllClick: (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        reportEvent('Home > Restarts > View All', { count: totalCount });

        router.push(
          Routes.commissioner({
            [URLQueryKeys.ACTIVE_TAB]: CommishActiveTab.FINAL,
            [URLQueryKeys.RESTART_STATUS]: true,
          })
        );
      },
    };
  }, [
    isRestartsLoading,
    contests,
    totalCount,
    dismissedContestIds,
    isFetching,
    isRedirecting,
    requestedURL,
    isDismissing,
    isRestartsFetching,
    dismissContestRestart,
    redirectToEO,
    router,
  ]);

  return {
    restartsProps,
    isRestartsLoading,
  };
};
